import { Button, Checkbox, Icon, InputGroup } from "@blueprintjs/core";
import React, { useState } from "react";
import { actionsColumnName } from "../../services/helpers/headers";
import { type IColumnDefsConfig } from "@focus/focusUI";
import type { ColumnOrderState, VisibilityState } from "./Table";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ToggleVisibilityArgs } from "./useFocusocTable";

interface Props {
  columnDefinitions: IColumnDefsConfig[];
  columnVisibility: VisibilityState;
  toggleVisibility: (args: ToggleVisibilityArgs) => void;
  columnOrder: ColumnOrderState;
  handleColumnOrderChange: (order: ColumnOrderState) => void;
}

const SortableItem: React.FC<{
  id: string;
  children: (args: {
    setNodeRef: (node: HTMLElement | null) => void;
    listeners: any;
    attributes: any;
    style: React.CSSProperties;
  }) => React.ReactNode;
}> = ({ id, children }) => {
  const { setNodeRef, listeners, attributes, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return <>{children({ setNodeRef, listeners, attributes, style })}</>;
};

const getColumnLabel = (
  columnDefinitions: IColumnDefsConfig[],
  columnId: string,
) =>
  ((columnDefinitions.find((c) => c.key === columnId)?.headerName ??
    columnId) as string);

export const TableSideBar: React.FC<Props> = ({
  columnDefinitions,
  columnVisibility,
  toggleVisibility,
  columnOrder,
  handleColumnOrderChange,
}) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");

  const filteredColumns = columnOrder.filter((columnId) => {
    if (columnId === actionsColumnName) return false;

    const header = getColumnLabel(columnDefinitions, columnId);

    return header.toLowerCase().includes(filter.toLowerCase());
  });

  const visibleColumns = filteredColumns.filter(
    (columnId) => columnVisibility[columnId],
  );

  const allVisible =
    filteredColumns.length > 0 &&
    visibleColumns.length === filteredColumns.length;

  const noneVisible = visibleColumns.length === 0;

  const isIndeterminate = !allVisible && !noneVisible;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  const onDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = columnOrder.indexOf(active.id);
    const newIndex = columnOrder.indexOf(over.id);

    const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
    handleColumnOrderChange(newOrder);
  };

  const toggleAllColumns = () => {
    const someVisible = filteredColumns.some((id) => columnVisibility[id]);

    toggleVisibility({
      ids: filteredColumns,
      visible: !someVisible,
    });
  };

  const visibilityClass = open
    ? "foc-aggrid-sidebar_container--open"
    : "foc-aggrid-sidebar_container--close";

  const visibleCount = columnOrder.filter(
    (columnId) =>
      columnId !== actionsColumnName && columnVisibility[columnId],
  ).length;

  const totalColumns = columnOrder.filter(
    (columnId) => columnId !== actionsColumnName,
  ).length;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={filteredColumns}
        strategy={verticalListSortingStrategy}
      >
        <aside className="foc-aggrid-sidebar">
          <button
            className="foc-aggrid-sidebar-test-and-icon"
            onClick={() => setOpen(!open)}
            title={open ? "Close columns" : "Configure columns"}
            type="button"
          >
            <span className="foc-aggrid-sidebar_icon">
              <Icon icon={open ? "cross" : "properties"} />
            </span>
            <span
              className="foc-aggrid-sidebar_text"
            >
              Columns
            </span>
          </button>

          <div className={`foc-aggrid-sidebar_container ${visibilityClass}`}>
            <div className="foc-aggrid-sidebar_container_form">
              <header className="border-b border-slate-200 px-4 py-4">
                <div className="text-xs font-semibold uppercase text-slate-500">
                  Table controls
                </div>
                <div className="mt-1 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-lg font-bold text-slate-950">
                      Columns
                    </h2>
                    <p className="mt-1 text-xs font-semibold text-slate-500">
                      {visibleCount} of {totalColumns} visible
                    </p>
                  </div>
                </div>
              </header>

              <div className="space-y-3 border-b border-slate-200 bg-slate-50/70 p-4">
                <InputGroup
                  className="table-sidebar-filter"
                  leftIcon="search"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFilter(e.target.value)
                  }
                  placeholder="Filter columns..."
                  rightElement={
                    filter ? (
                      <Button
                        icon="cross"
                        minimal
                        onClick={() => setFilter("")}
                        title="Clear filter"
                      />
                    ) : undefined
                  }
                  value={filter}
                />

                <div className="table-sidebar-toggle-all">
                  <Checkbox
                    checked={allVisible}
                    indeterminate={isIndeterminate}
                    label="Show all filtered columns"
                    onChange={toggleAllColumns}
                  />
                </div>
              </div>

              <div className="foc-aggrid-sidebar_container_cb">
                {filteredColumns.length === 0 && (
                  <div className="flex h-full items-center justify-center p-6 text-center text-sm font-semibold text-slate-500">
                    No columns found.
                  </div>
                )}

                {filteredColumns.map((columnId) => {
                  const columnName = getColumnLabel(
                    columnDefinitions,
                    columnId,
                  );
                  const visible = Boolean(columnVisibility[columnId]);

                  return (
                    <SortableItem key={columnId} id={columnId}>
                      {({ setNodeRef, listeners, attributes, style }) => (
                        <div
                          ref={setNodeRef}
                          style={style}
                          className="table-sidebar-column-row"
                        >
                          {!filter && (
                            <button
                              {...listeners}
                              {...attributes}
                              className="table-sidebar-drag-handle"
                              onClick={(e) => e.stopPropagation()}
                              title="Drag to reorder"
                              type="button"
                            >
                              <Icon icon="drag-handle-vertical" />
                            </button>
                          )}

                          <Checkbox
                            checked={visible}
                            className="table-sidebar-checkbox"
                            label={columnName}
                            onChange={() => toggleVisibility({ id: columnId })}
                          />

                          <span
                            className={`table-sidebar-state ${
                              visible
                                ? "table-sidebar-state-visible"
                                : "table-sidebar-state-hidden"
                            }`}
                          >
                            {visible ? "On" : "Off"}
                          </span>
                        </div>
                      )}
                    </SortableItem>
                  );
                })}
              </div>
            </div>
          </div>
        </aside>
      </SortableContext>
    </DndContext>
  );
};
